import { message } from "antd";

import type { CopySelectionOptionsResponse } from "@btnet/services-btnet/handlers/models/copySelectionOptionsResponse";

import { formattedPlural } from "utilities/string/string";

export interface IOutcomeCounts {
    successCount: number;
    failureCount: number;
}

export type OutcomeNoun = "options" | "allowances" | "items";

export function getCopyOutcomeCounts(response: CopySelectionOptionsResponse): IOutcomeCounts {
    const successCount = response.newOptions.filter((option) => option.newOption != null).length;
    const failureCount = Object.keys(response.failedSelectionOptionIds ?? {}).length;

    return {
        successCount,
        failureCount,
    };
}

export function getEntityNoun(allowanceCount: number, optionCount: number): OutcomeNoun {
    if (allowanceCount > 0 && optionCount > 0) return "items";
    if (allowanceCount > 0) return "allowances";
    return "options";
}

// Adds allowance counts (from createdAllowances) on top of getCopyOutcomeCounts' option counts.
export function getLibraryCopyOutcomeCounts(
    response: CopySelectionOptionsResponse,
    requestedGroupIds: number[],
): IOutcomeCounts {
    const { allowanceSuccessCount, allowanceFailureCount, optionCounts } = getLibraryCopyBreakdown(
        response,
        requestedGroupIds,
    );

    return {
        successCount: allowanceSuccessCount + optionCounts.successCount,
        failureCount: allowanceFailureCount + optionCounts.failureCount,
    };
}

// Picks the toast noun for a library copy from what actually copied, not just what was
// requested, so an allowance whose sub-options tagged along (or failed) still reads as "items"
export function getLibraryCopyOutcomeNoun(
    response: CopySelectionOptionsResponse,
    requestedGroupIds: number[],
): OutcomeNoun {
    const { allowanceSuccessCount, allowanceFailureCount, optionCounts } = getLibraryCopyBreakdown(
        response,
        requestedGroupIds,
    );
    const hasAllowances = allowanceSuccessCount + allowanceFailureCount > 0;
    const hasOptions = optionCounts.successCount + optionCounts.failureCount > 0;
    return getEntityNoun(hasAllowances ? 1 : 0, hasOptions ? 1 : 0);
}

function getLibraryCopyBreakdown(
    response: CopySelectionOptionsResponse,
    requestedGroupIds: number[],
) {
    const createdGroupIds = new Set(
        (response.createdAllowances ?? []).map((allowance) => Number(allowance.sourceGroupId)),
    );
    const allowanceSuccessCount = requestedGroupIds.filter((id) => createdGroupIds.has(id)).length;
    const allowanceFailureCount = requestedGroupIds.length - allowanceSuccessCount;

    const optionCounts = getCopyOutcomeCounts(response);

    return { allowanceSuccessCount, allowanceFailureCount, optionCounts };
}

function getOutcomePresentation(
    { successCount, failureCount }: IOutcomeCounts,
    noun: OutcomeNoun,
    totalCount?: number,
) {
    const total = totalCount ?? successCount + failureCount;

    let messageType: "success" | "warning" | "error";
    if (failureCount > 0) {
        messageType = successCount > 0 ? "warning" : "error";
    } else {
        messageType = "success";
    }

    // "items" is intentionally not pluralized — used only for mixed allowance + option totals
    const pluralNoun =
        noun === "items"
            ? "items"
            : formattedPlural({
                  value: total,
                  one: noun === "allowances" ? "allowance" : "option",
                  other: noun,
              });

    return { messageType, pluralNoun, total };
}

export function showCopyOutcomeMessage(
    counts: IOutcomeCounts,
    noun: OutcomeNoun,
    totalCount?: number,
): void {
    const { messageType, pluralNoun, total } = getOutcomePresentation(counts, noun, totalCount);
    message[messageType](`${counts.successCount} of ${total} ${pluralNoun} copied`);
}

export function showSaveToCatalogOutcomeMessage(
    counts: IOutcomeCounts,
    noun: OutcomeNoun,
    warnings?: string[],
): void {
    const { messageType: baseType, pluralNoun, total } = getOutcomePresentation(counts, noun);
    const hasWarnings = (warnings?.length ?? 0) > 0;
    const messageType = hasWarnings && baseType === "success" ? "warning" : baseType;

    const singularStatus = counts.successCount === 1 ? "saved" : "not saved";
    const countText =
        total === 1
            ? `${getSingularNoun(noun)} ${singularStatus} to catalog`
            : `${counts.successCount} of ${total} ${pluralNoun} saved to catalog`;

    const warningsSuffix = warnings && warnings.length > 0 ? `. ${warnings.join(" ")}` : "";

    message[messageType](`${countText}${warningsSuffix}`);
}

function getSingularNoun(noun: OutcomeNoun): string {
    if (noun === "allowances") return "Allowance";
    if (noun === "items") return "Item";
    return "Option";
}

export function showAddFromCatalogOutcomeMessage(successCount: number): void {
    if (successCount <= 0) {
        return;
    }
    if (successCount === 1) {
        message.success("1 item added");
        return;
    }
    message.success(`${successCount} items added`);
}
